import {
  Container,
  Heading,
  Field,
  Input,
  Button,
  Checkbox,
  Stack,
  Spinner,
  Alert,
  ButtonGroup,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { useLogout } from "../../hooks/useLogout";
import type { Camelize } from "camelize-ts";
import {
  useUpdateUserProfile,
  useUserProfile,
} from "../../hooks/useUserProfile";
import { Plus, Minus } from "lucide-react";
import { UserProfileSchema } from "../../generated";
import { useSession } from "../../hooks/useSession";
import { useBrreg } from "../../hooks/useBrreg";
import { useEffect } from "react";
import IssuerModal from "../efaktura/issuer/IssuerModal";
import OcrModal from "../ocr/OcrModal";

const bankAccountSchema = yup.object({
  bankAccountNumber: yup
    .string()
    .matches(
      /^(?:\d{4}\.\d{2}\.\d{5}|\d{11})$/,
      'Bank account number must be in the format "1234.11.12345" or "12341112345"',
    )
    .required(),
  isPrimary: yup.boolean().required(),
});

const schema = yup
  .object({
    companyName: yup.string().required(),
    orgNumber: yup.string().length(9).required(),
    vatRegistered: yup.boolean().required(),
    foretaksregisteret: yup.boolean().required(),
    email: yup.string().email().required(),
    billingAddress1: yup.string().required(),
    billingAddress2: yup.string().optional(),
    billingPostalCode: yup.string().min(3).max(5).required(),
    billingCity: yup.string().required(),
    startInvoiceNumber: yup.number().positive(),
    bankAccounts: yup.array().of(bankAccountSchema).required(),
  })
  .required();

export default function ProfilePage() {
  useSession();
  const userProfileQuery = useUserProfile();
  const updateUserProfile = useUpdateUserProfile();

  const bankAccounts = userProfileQuery.data?.bankAccounts || [];

  const methods = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    values: {
      ...userProfileQuery.data,
      companyName: userProfileQuery.data?.companyName || "",
      orgNumber: userProfileQuery.data?.orgNumber || "",
      vatRegistered: userProfileQuery.data?.vatRegistered || false,
      foretaksregisteret: userProfileQuery.data?.foretaksregisteret || false,
      billingAddress1: userProfileQuery.data?.billingAddress1 || "",
      billingAddress2: userProfileQuery.data?.billingAddress2 || "",
      billingPostalCode: userProfileQuery.data?.billingPostalCode || "",
      billingCity: userProfileQuery.data?.billingCity || "",
      email: userProfileQuery.data?.email || "",
      startInvoiceNumber:
        userProfileQuery.data?.startInvoiceNumber || undefined,
      bankAccounts:
        bankAccounts.length > 0
          ? bankAccounts
          : [{ bankAccountNumber: "", isPrimary: true }],
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors: formStateErrors },
    control,
    setValue,
    getValues,
    watch,
    reset,
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "bankAccounts",
    rules: { minLength: 1 },
  });
  watch("bankAccounts");
  watch("orgNumber");
  const bankAccountFields = getValues("bankAccounts");

  const orgNumber = getValues("orgNumber");
  const brreg = useBrreg(orgNumber);
  const logout = useLogout();

  useEffect(() => {
    if (
      orgNumber.length === 9 &&
      userProfileQuery.data?.orgNumber !== orgNumber
    ) {
      const values = getValues();
      reset({
        ...values,
        companyName: values?.companyName || brreg.data?.navn,
        billingAddress1:
          values?.billingAddress1 ||
          brreg.data?.beliggenhetsadresse.adresse?.[0],
        billingCity:
          values?.billingCity || brreg.data?.beliggenhetsadresse.poststed,
        billingPostalCode:
          values?.billingPostalCode ||
          brreg.data?.beliggenhetsadresse.postnummer,
      });
    }
  }, [
    orgNumber,
    brreg.data,
    userProfileQuery.data?.orgNumber,
    getValues,
    reset,
  ]);

  const onSubmit = (data: Camelize<UserProfileSchema>) => {
    return updateUserProfile.mutate(data);
  };

  if (userProfileQuery.isLoading) {
    return (
      <Container display="flex" justifyContent="center">
        <Spinner />
      </Container>
    );
  }

  return (
    <Stack display="flex">
      <Heading as="h1" mb={4}>
        Profil
      </Heading>
      {!userProfileQuery.data?.isProfileCompleted && (
        <Alert.Root>Fullfør profilen før du kan lage fakturaer</Alert.Root>
      )}
      <Stack direction="row">
        <IssuerModal />
        <OcrModal />
      </Stack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack minW={350} pt={4}>
          <Field.Root invalid={Boolean(formStateErrors["orgNumber"])}>
            <Field.Label>Organisasjonsnummer</Field.Label>
            <Stack direction="row">
              <Input {...register("orgNumber")} placeholder="987654321" />
            </Stack>
          </Field.Root>
          <Field.Root invalid={Boolean(formStateErrors["companyName"])}>
            <Field.Label>Bedriftsnavn</Field.Label>
            <Input {...register("companyName")} placeholder="Faktura AS" />
          </Field.Root>
          <Field.Root invalid={Boolean(formStateErrors["vatRegistered"])}>
            <Stack direction="row">
              <Field.Label width={150}>MVA-registrert</Field.Label>
              <Checkbox.Root
                size="lg"
                checked={watch("vatRegistered")}
                onCheckedChange={(details) => {
                  setValue("vatRegistered", Boolean(details.checked));
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
            </Stack>
          </Field.Root>
          <Field.Root
            invalid={Boolean(formStateErrors["foretaksregisteret"])}
          >
            <Stack direction="row">
              <Field.Label width={150}>Foretaksregisteret</Field.Label>
              <Checkbox.Root
                size="lg"
                checked={watch("foretaksregisteret")}
                onCheckedChange={(details) => {
                  setValue("foretaksregisteret", Boolean(details.checked));
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
            </Stack>
          </Field.Root>
          <Field.Root invalid={Boolean(formStateErrors["email"])}>
            <Field.Label>E-post</Field.Label>
            <Input {...register("email")} placeholder="accounting@faktura" />
          </Field.Root>
          <Field.Root invalid={Boolean(formStateErrors["billingAddress1"])}>
            <Field.Label>Adresse</Field.Label>
            <Input
              {...register("billingAddress1")}
              placeholder="Slottsplassen 1"
            />
          </Field.Root>
          <Field.Root invalid={Boolean(formStateErrors["billingAddress2"])}>
            <Field.Label>Adresselinje 2 (valgfritt)</Field.Label>
            <Input {...register("billingAddress2")} placeholder="" />
          </Field.Root>
          <Field.Root
            invalid={Boolean(formStateErrors["billingPostalCode"])}
          >
            <Field.Label>Postkode</Field.Label>
            <Input {...register("billingPostalCode")} placeholder="0001" />
          </Field.Root>
          <Field.Root invalid={Boolean(formStateErrors["billingCity"])}>
            <Field.Label>By</Field.Label>
            <Input {...register("billingCity")} placeholder="Oslo" />
          </Field.Root>
          <Field.Root
            invalid={Boolean(formStateErrors["startInvoiceNumber"])}
          >
            <Field.Label>Første fakturanummer (kan kun settes en gang)</Field.Label>
            <Input
              type="number"
              {...register("startInvoiceNumber")}
              min={1}
              disabled={Boolean(userProfileQuery.data?.hasCreatedInvoice)}
            />
          </Field.Root>
          {fields.map((field, i) => {
            const bankAccount = bankAccountFields?.[i];
            return (
              <Stack key={field.id} gap="2" direction="row">
                <Field.Root
                  invalid={Boolean(
                    formStateErrors?.bankAccounts?.[i]?.bankAccountNumber
                      ?.message,
                  )}
                >
                  <Field.Label>Kontonummer</Field.Label>
                  <Input
                    {...register(`bankAccounts.${i}.bankAccountNumber`)}
                    placeholder="1111.11.11111"
                  />
                </Field.Root>
                <Field.Root
                  invalid={Boolean(
                    formStateErrors?.bankAccounts?.[i]?.isPrimary?.message,
                  )}
                  onClick={() => {
                    fields.forEach((_, fieldIndex) => {
                      if (i !== fieldIndex) {
                        setValue(`bankAccounts.${fieldIndex}.isPrimary`, false);
                      }
                    });
                  }}
                >
                  {i === 0 && <Field.Label>Primærkonto</Field.Label>}
                  <Checkbox.Root
                    size="lg"
                    mt={i !== 0 ? 8 : 0}
                    checked={bankAccount.isPrimary}
                    onCheckedChange={(details) => {
                      setValue(`bankAccounts.${i}.isPrimary`, Boolean(details.checked));
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                  </Checkbox.Root>
                </Field.Root>
                <Button
                  onClick={() => {
                    remove(i);
                  }}
                >
                  <Minus />
                </Button>
              </Stack>
            );
          })}
          <Button
            onClick={() => {
              append({ bankAccountNumber: "", isPrimary: false });
            }}
          >
            <Plus />
          </Button>
        </Stack>
        <Stack direction="row" py={8}>
          <ButtonGroup>
            <Button type="submit">Oppdater</Button>
            <Button onClick={() => logout()} colorPalette="red">
              Logg ut
            </Button>
          </ButtonGroup>
        </Stack>
      </form>
    </Stack>
  );
}
